import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useState } from "react";
import { tokens } from "../../theme";
import { PageHeader } from "../../components/PageHeader";
import { describeError, useToast } from "../../components/toast";
import type { ActionKey } from "../../components/toast/ToastProvider";
import { ItemFormSurface } from "../../components/EditDrawer";
import {
  foyerApi,
  type OwnerTeamInvite,
  type OwnerTeamMember,
} from "../../http/foyer";
import { friendlyError } from "../../utils/apiError";
import type { Translate } from "../../i18n/translate";
import { OwnerGate } from "../../components/supplier/OwnerGate";
import { useFormat } from "../../i18n/format";

/**
 * The organisation's own people (`CONTENT-23`).
 *
 * A content owner is an organisation, not a user. Nenda creates the
 * organisation and appoints its team admin; the team admin invites and removes
 * colleagues here, without asking Nenda.
 *
 * Everything that decides SCOPE happens on the server: the invite is bound to
 * this session's organisation and carries the standard owner rights, whatever
 * this page sends. The page only asks for an email.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function failText(
  t: Translate,
  action: ActionKey,
  e: unknown,
  params: Record<string, string>
): string {
  return describeError(t, action, friendlyError(t, e), params);
}

export default function SupplierTeamPage() {
  const toast = useToast();
  const { t } = useTranslation(["supplier", "common"]);
  const fmt = useFormat();
  const [members, setMembers] = useState<OwnerTeamMember[]>([]);
  const [invites, setInvites] = useState<OwnerTeamInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sentLink, setSentLink] = useState<string | null>(null);
  /** The Invite modal: always mounted, the field reset when it OPENS. */
  const [inviteOpen, setInviteOpen] = useState(false);
  /** A refusal of the invite itself, shown inside the modal. */
  const [inviteError, setInviteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const team = await foyerApi.getOwnerTeam();
      setMembers(team.members);
      setInvites(team.invites);
      setError(null);
    } catch (e) {
      setError(friendlyError(t, e));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Resolves true once sent, so the modal can close itself. */
  const invite = async (): Promise<boolean> => {
    setSending(true);
    setInviteError(null);
    setSentLink(null);
    try {
      const created = await foyerApi.inviteOwnerColleague(email.trim());
      setSentLink(created.acceptUrl ?? null);
      toast.success(t("supplierTeamPage.inviteSent", { email: email.trim() }));
      await load();
      return true;
    } catch (e) {
      setInviteError(friendlyError(t, e));
      toast.error(failText(t, "inviteTeamMember", e, { email: email.trim() }));
      return false;
    } finally {
      setSending(false);
    }
  };

  const withdraw = async (inv: OwnerTeamInvite) => {
    if (
      !window.confirm(
        t("supplierTeamPage.withdrawConfirm", { email: inv.email })
      )
    )
      return;
    try {
      await foyerApi.withdrawOwnerInvite(inv.id);
      toast.success(
        t("supplierTeamPage.inviteWithdrawn", { email: inv.email })
      );
      await load();
    } catch (e) {
      toast.error(failText(t, "withdrawTeamInvite", e, { email: inv.email }));
    }
  };

  const remove = async (m: OwnerTeamMember) => {
    if (
      !window.confirm(t("supplierTeamPage.removeConfirm", { email: m.email }))
    ) {
      return;
    }
    try {
      await foyerApi.removeOwnerColleague(m.id);
      toast.success(t("supplierTeamPage.removed", { email: m.email }));
      await load();
    } catch (e) {
      toast.error(failText(t, "removeTeamMember", e, { email: m.email }));
    }
  };

  return (
    <Box>
      <PageHeader
        title={t("supplierTeamPage.title")}
        subtitle={t("supplierTeamPage.subtitle")}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="team-error">
          {error}
        </Alert>
      )}

      <OwnerGate resource="users" level="edit">
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              setEmail("");
              setInviteError(null);
              setInviteOpen(true);
            }}
            data-testid="team-invite-open"
          >
            {t("supplierTeamPage.invite")}
          </Button>
        </Box>
      </OwnerGate>

      <ItemFormSurface
        mode="create"
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={t("supplierTeamPage.inviteTitle")}
        maxWidth="xs"
        busy={sending}
        footerTop={
          inviteError && (
            <Alert severity="error" sx={{ mb: 1 }} data-testid="team-error">
              {inviteError}
            </Alert>
          )
        }
        actions={(dismiss) => (
          <>
            <Button onClick={() => dismiss()} disabled={sending}>
              {t("common:buttons.cancel")}
            </Button>
            <OwnerGate
              resource="users"
              level="edit"
              deniedProps={{ disabled: true }}
            >
              <Button
                variant="contained"
                onClick={() => void invite().then((ok) => ok && dismiss())}
                disabled={!EMAIL.test(email.trim()) || sending}
                data-testid="team-invite-send"
              >
                {t("supplierTeamPage.invite")}
              </Button>
            </OwnerGate>
          </>
        )}
      >
        <TextField
          size="small"
          label={t("supplierTeamPage.emailLabel")}
          autoFocus
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mt: 1 }}
          inputProps={{ "data-testid": "team-invite-email" }}
          helperText={t("supplierTeamPage.emailHelp")}
        />
      </ItemFormSurface>

      {sentLink && (
        <Alert severity="success" sx={{ mb: 2, maxWidth: 560 }}>
          {t("supplierTeamPage.sentShareLink")}{" "}
          <Box component="span" sx={{ wordBreak: "break-all" }}>
            {sentLink}
          </Box>
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={22} />
      ) : (
        <Stack gap={3} sx={{ mt: 2, maxWidth: 560 }}>
          <Box>
            <Typography sx={{ fontWeight: "fontWeightSemibold", mb: 1 }}>
              {t("supplierTeamPage.people")}
            </Typography>
            <Stack gap={0.5}>
              {members.map((m) => (
                <Stack
                  key={m.id}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  data-testid={`team-member-${m.email}`}
                  sx={{
                    p: 1,
                    border: `1px solid ${tokens.divider}`,
                    borderRadius: "8px",
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {m.email}
                  </Typography>
                  {m.isSelf && (
                    <Chip size="small" label={t("supplierTeamPage.you")} />
                  )}
                  {m.isTeamAdmin && (
                    <Chip
                      size="small"
                      label={t("supplierTeamPage.teamAdmin")}
                    />
                  )}
                  {!m.isSelf && !m.isTeamAdmin && (
                    <OwnerGate resource="users" level="edit">
                      <Tooltip title={t("supplierTeamPage.removeTooltip")}>
                        <IconButton
                          size="small"
                          aria-label={t("supplierTeamPage.removeAria", {
                            email: m.email,
                          })}
                          onClick={() => void remove(m)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </OwnerGate>
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: "fontWeightSemibold", mb: 1 }}>
              {t("supplierTeamPage.waiting")}
            </Typography>
            {invites.length === 0 ? (
              <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
                {t("supplierTeamPage.noInvites")}
              </Typography>
            ) : (
              <Stack gap={0.5}>
                {invites.map((inv) => (
                  <Stack
                    key={inv.id}
                    direction="row"
                    alignItems="center"
                    gap={1}
                    data-testid={`team-invite-${inv.email}`}
                    sx={{
                      p: 1,
                      border: `1px dashed ${tokens.divider}`,
                      borderRadius: "8px",
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {inv.email}
                    </Typography>
                    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                      {t("supplierTeamPage.expires", {
                        date: fmt.date(inv.expiresAt),
                      })}
                    </Typography>
                    <OwnerGate resource="users" level="edit">
                      <Tooltip title={t("supplierTeamPage.withdrawTooltip")}>
                        <IconButton
                          size="small"
                          aria-label={t("supplierTeamPage.withdrawAria", {
                            email: inv.email,
                          })}
                          onClick={() => void withdraw(inv)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </OwnerGate>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
